import styled from '@emotion/styled';
import {
  Button,
  ButtonProps,
  Layout,
  Popover,
  SiderProps,
  Tooltip,
} from 'antd';
import { elementSizeAtomFam } from 'packages/lib/common-ui/state';
import { SyncAreaSize } from 'packages/lib/common-ui/sync-area-size/sync-area-size';
import { toolbarAreaId } from 'packages/at-shell/components/app-shell/app-layout-constants';
import { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { isToolbarCollapsedAtom } from 'packages/at-shell/state/recoil/ui';

const { Sider } = Layout;

const buttonSize = 32;

const StyledToolButton = styled(Button)`
  border-radius: 2px;
  width: ${buttonSize}px;
  height: ${buttonSize}px;
  min-width: ${buttonSize}px;
  min-height: ${buttonSize}px;
  max-width: ${buttonSize}px;
  max-height: ${buttonSize}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ToolbarSlot = styled.div`
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  width: ${buttonSize}px;
  height: ${buttonSize}px;
  min-width: ${buttonSize}px;
  min-height: ${buttonSize}px;
  max-width: ${buttonSize}px;
  max-height: ${buttonSize}px;
`;

const ToolButtonWrapper = styled.div`
  margin: 0.25rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ToolGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ToolbarContent = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0;
`;

export const ToolButton = (
  props: ButtonProps & { label?: string }
): JSX.Element => {
  const { label, ...restProps } = props;
  return (
    <Tooltip placement="right" title={label}>
      <ToolButtonWrapper>
        <StyledToolButton {...restProps} />
      </ToolButtonWrapper>
    </Tooltip>
  );
};

export const ToolPopoverButton = (
  props: ButtonProps & { label?: string | ReactNode; open?: boolean }
): JSX.Element => {
  const { children, label, open, ...otherProps } = props;
  return (
    <Popover
      placement="right"
      title={label}
      content={children}
      open={open}
      onOpenChange={(nextValue) => {
        return nextValue;
      }}
    >
      <ToolButtonWrapper>
        <StyledToolButton {...otherProps} />
      </ToolButtonWrapper>
    </Popover>
  );
};

export const AppToolbar = ({
  tools,
  siderProps,
}: {
  tools: ReactNode;
  siderProps: SiderProps;
}): JSX.Element => {
  const collapsed = useRecoilValue(isToolbarCollapsedAtom);
  return (
    <Sider
      {...siderProps}
      collapsed={
        siderProps.collapsed !== undefined ? siderProps.collapsed : collapsed
      }
      id="at-shell-toolbar-sider"
      style={{
        position: 'relative',
        zIndex: 100,
        height: '100%',
        width: '100%',
        padding: 0,
      }}
    >
      <SyncAreaSize
        id={toolbarAreaId}
        elementSizeAtomFam={elementSizeAtomFam}
      />
      <ToolbarContent>
        <ToolGroup>
          {tools ??
            Array.from({ length: 10 }, (_, i) => i).map((n) => (
              <ToolbarSlot key={`toolbar-slot-${n}`} id={`toolbar-slot-${n}`} />
            ))}
        </ToolGroup>
      </ToolbarContent>
    </Sider>
  );
};
